import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { getLocationsByRegion, getWeather } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { LoadingState, WeatherIcon, Spinner, SEO } from '@/components';
import { FaChevronLeft, FaStar, FaRegStar } from 'react-icons/fa';
import type { Location } from '@miweather/shared';
import { useFavorites } from '@/hooks';

// Region metadata
const regionInfo: Record<string, { name: string; description: string }> = {
  'southeast': {
    name: 'Southeast Michigan',
    description: 'Metro Detroit, Ann Arbor, Flint, and surrounding areas'
  },
  'thumb': {
    name: 'The Thumb',
    description: 'Port Huron, Bad Axe, and the thumb region'
  },
  'southwest': {
    name: 'Southwest Michigan',
    description: 'Kalamazoo, Battle Creek, St. Joseph, and the Indiana border'
  },
  'west': {
    name: 'West Michigan',
    description: 'Grand Rapids, Holland, Muskegon, and the Lake Michigan shore'
  },
  'mid-michigan': {
    name: 'Mid-Michigan',
    description: 'Lansing, Saginaw, Bay City, and central Michigan'
  },
  'northern-lp': {
    name: 'Northern Lower Peninsula',
    description: 'Traverse City, Petoskey, Gaylord, and northern resort areas'
  },
  'upper-peninsula': {
    name: 'Upper Peninsula',
    description: 'Marquette, Sault Ste. Marie, and the UP wilderness'
  },
};

// City images
const cityImages: Record<string, string> = {
  'detroit': 'https://images.unsplash.com/photo-1564164830657-eeb0e0875f5b?w=400&h=250&fit=crop',
  'grand-rapids': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=250&fit=crop',
  'traverse-city': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=250&fit=crop',
  'marquette': 'https://images.unsplash.com/photo-1478827536114-da961b7f86d2?w=400&h=250&fit=crop',
  'lansing': 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=400&h=250&fit=crop',
  'ann-arbor': 'https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=400&h=250&fit=crop',
};
const defaultImage = 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=400&h=250&fit=crop';

export function RegionPage() {
  const { regionId } = useParams({ from: '/region/$regionId' });
  const { isFavorite, toggleFavorite } = useFavorites();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.locations.byRegion(regionId),
    queryFn: () => getLocationsByRegion(regionId),
  });

  const region = regionInfo[regionId];

  if (isLoading) {
    return <LoadingState message="Loading region..." />;
  }

  if (error || !data || !region) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-[var(--color-error)] mb-4">Region not found</h2>
        <Link to="/" className="text-[var(--color-info)] hover:underline">← Back to home</Link>
      </div>
    );
  }

  const cities = data.locations.filter(loc => loc.type === 'city');
  const counties = data.locations.filter(loc => loc.type === 'county');

  return (
    <div className="space-y-6">
      <SEO
        title={`${region.name} Weather`}
        description={`Weather forecasts for ${region.name}. ${region.description}. Current conditions, hourly and 7-day forecasts for all cities and counties.`}
        path={`/region/${regionId}`}
      />
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/"
          className="p-2 rounded-lg stat-box hover:bg-white/10 transition-colors"
        >
          <FaChevronLeft className="text-white/70" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{region.name}</h1>
          <p className="text-white/60 text-sm">{region.description}</p>
        </div>
      </div>

      {/* Cities */}
      {cities.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mb-3">Cities</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {cities.map((city) => (
              <LocationCard
                key={city.id}
                location={city}
                isFavorite={isFavorite(city.id)}
                onToggleFavorite={() => toggleFavorite(city.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Counties */}
      {counties.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mb-3">Counties</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {counties.map((county) => (
              <CountyCard
                key={county.id}
                location={county}
                isFavorite={isFavorite(county.id)}
                onToggleFavorite={() => toggleFavorite(county.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface LocationCardProps {
  location: Location;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

function LocationCard({ location, isFavorite = false, onToggleFavorite }: LocationCardProps) {
  const image = cityImages[location.id] || defaultImage;

  // Fetch real weather data for this location
  const { data: weatherData, isLoading } = useQuery({
    queryKey: queryKeys.weather.byLocation(location.id),
    queryFn: () => getWeather(location.id),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const handleStarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite?.();
  };

  return (
    <Link to="/weather/$locationId" params={{ locationId: location.id }}>
      <div
        className="relative h-32 rounded-2xl overflow-hidden hover:ring-2 hover:ring-[var(--color-info)] transition-all cursor-pointer bg-cover bg-center"
        style={{ backgroundImage: `url(${image})` }}
      >
        <div className="absolute inset-0 card-image-overlay" />
        <div className="relative h-full p-3 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-bold text-base">{location.name}</h3>
              {isLoading ? (
                <p className="text-xs text-white/50">Loading...</p>
              ) : weatherData ? (
                <p className="text-xs text-white/70">{weatherData.current.conditions}</p>
              ) : (
                <p className="text-xs text-white/50">--</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onToggleFavorite && (
                <button
                  onClick={handleStarClick}
                  className="p-1 hover:scale-110 transition-transform"
                  aria-label={isFavorite ? `Remove ${location.name} from favorites` : `Add ${location.name} to favorites`}
                  aria-pressed={isFavorite}
                >
                  {isFavorite ? (
                    <FaStar className="text-yellow-400" aria-hidden="true" />
                  ) : (
                    <FaRegStar className="text-white/50 hover:text-yellow-400" aria-hidden="true" />
                  )}
                </button>
              )}
              {weatherData && (
                <WeatherIcon icon={weatherData.current.icon} size={24} className="text-white/80" />
              )}
              {isLoading && <Spinner size="sm" />}
            </div>
          </div>
          <div className="text-2xl font-black">
            {isLoading ? '--' : weatherData ? `${weatherData.current.temperature ?? '--'}°F` : '--'}
          </div>
        </div>
      </div>
    </Link>
  );
}

interface CountyCardProps {
  location: Location;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

function CountyCard({ location, isFavorite = false, onToggleFavorite }: CountyCardProps) {
  const handleStarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite?.();
  };

  return (
    <Link to="/weather/$locationId" params={{ locationId: location.id }}>
      <div className="stat-box rounded-2xl p-4 hover:bg-white/10 transition-colors cursor-pointer">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-base">{location.name}</h3>
            <p className="text-xs text-white/50">View forecast</p>
          </div>
          {onToggleFavorite && (
            <button
              onClick={handleStarClick}
              className="p-1 hover:scale-110 transition-transform"
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              {isFavorite ? (
                <FaStar className="text-yellow-400" />
              ) : (
                <FaRegStar className="text-white/50 hover:text-yellow-400" />
              )}
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
